import { UserManager } from 'oidc-client-ts';
import { graphOidcConfig, graphEntraConfigured } from './graphConfig';

// Lazily constructed — building a UserManager touches window.location at
// module load, which is fine, but there's no reason to pay for it (or the
// storage read it does internally) until a search actually needs a token.
let manager: UserManager | null = null;
function graphManager(): UserManager {
  if (!manager) manager = new UserManager(graphOidcConfig);
  return manager;
}

const EXPIRED_MESSAGE =
  'Your Microsoft directory sign-in has expired — toggle directory search off and on again to reconnect.';

/**
 * Returns a delegated Graph access token (`User.ReadBasic.All`), acquiring
 * one via `signinPopup()` if none is cached. MUST be called from a user
 * gesture (a click handler): the popup is opened a microtask or two after
 * the click, well inside the browser's user-activation window — callers
 * should only invoke this from one.
 *
 * Deliberately no `signinSilent()` first. The shell's CSP has no `frame-src`
 * (so `login.microsoftonline.com` can't load in an iframe) and every
 * response carries `frame-ancestors 'none'` + `X-Frame-Options: DENY` (so
 * even `/auth/graph-callback` can't load in one). The hidden-iframe renew
 * can therefore never succeed — it only ever times out after 10s, by which
 * point the click's activation has expired, the browser blocks the popup,
 * and oidc-client-ts throws "Attempted to navigate on a disposed window".
 * When the Entra SSO session is live the popup closes itself almost
 * immediately anyway.
 */
export async function getGraphAccessToken(): Promise<string> {
  if (!graphEntraConfigured) {
    throw new Error('Entra is not configured in this environment (VITE_ENTRA_ISSUER_URL/VITE_ENTRA_CLIENT_ID) — Graph search is unavailable.');
  }
  const mgr = graphManager();
  const existing = await mgr.getUser();
  if (existing && !existing.expired) return existing.access_token;

  try {
    const popup = await mgr.signinPopup();
    return popup.access_token;
  } catch (e) {
    if ((e as Error).message.includes('disposed window')) {
      throw new Error('The browser blocked the Microsoft sign-in popup — allow pop-ups for this site, then toggle directory search on again.');
    }
    throw e;
  }
}

/**
 * The already-acquired token, for code that runs outside a user gesture
 * (the debounced search). Never opens a popup — one opened from a timer
 * would just be blocked — so an expired token surfaces as an error asking
 * for a fresh click instead.
 */
export async function getCachedGraphAccessToken(): Promise<string> {
  const existing = await graphManager().getUser();
  if (existing && !existing.expired) return existing.access_token;
  throw new Error(EXPIRED_MESSAGE);
}

export { graphManager };
